import { useCallback, useEffect, useRef } from "react";

/** Room above the question once it reaches the top of the thread. */
const FOLLOW_MARGIN_PX = 16;

/**
 * Keeps the start of the latest turn in view while it grows: the question rises
 * to the top of the thread and the answer reads down from it, instead of the
 * thread chasing the end and scrolling the start of the answer away. Following
 * starts again for each new turn (`latestKey`) and stops once the person
 * scrolls or clicks in the thread themselves.
 *
 * Only the thread scrolls. `scrollIntoView` would also scroll the panel around
 * it (its frame clips with `overflow: hidden`, which code can still scroll) and
 * push the composer up.
 */
export function useFollowLatestTurn(latestKey: string) {
  const threadRef = useRef<HTMLDivElement>(null);
  const latestRef = useRef<HTMLDivElement>(null);
  const isFollowingRef = useRef(true);

  useEffect(() => {
    const thread = threadRef.current;
    const latest = latestRef.current;
    if (!thread || !latest) return;
    isFollowingRef.current = true;
    const follow = () => {
      if (!isFollowingRef.current) return;
      const offset = latest.getBoundingClientRect().top - thread.getBoundingClientRect().top;
      thread.scrollTo?.({ top: thread.scrollTop + offset - FOLLOW_MARGIN_PX });
    };
    follow();
    // Text, cards and images arrive after the turn first renders; each one can still move the question up.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(follow);
    observer.observe(latest);
    return () => observer.disconnect();
  }, [latestKey]);

  const stopFollowing = useCallback(() => {
    isFollowingRef.current = false;
  }, []);

  return { threadRef, latestRef, stopFollowing };
}
